import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import PropTypes from 'prop-types';
import {
    Fab,
    Paper,
    Box,
    Typography,
    IconButton,
    TextField,
    CircularProgress,
    Chip,
    Tooltip
} from '@material-ui/core';

import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import MoveIcon from '@material-ui/icons/ControlCamera';
import SendIcon from '@material-ui/icons/Send';
import RefreshIcon from '@material-ui/icons/Refresh';
import { AI_AGENT_API_KEY, CUSTWEB_ROOT, REGION, URL } from 'config/config';
import { renderMarkDown } from 'utils/renderMarkDown';
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import { useAiDocs } from './useAIDocs';
import Draggable from "react-draggable";

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
    tooltipPopper: {
        zIndex: 10001
    }
}));

const OPENROUTER_MODEL = 'nvidia/nemotron-3-ultra-550b-a55b:free'; //'nvidia/nemotron-3-ultra-550b-a55b';

const createInitialMessages = () => ([
    {
        role: 'assistant',
        content:
            'Hi! I can help you set up your organization, services, provider accounts, and schedules in Quezone. What are you trying to configure today?'
    }
]);

const getAssistantContent = response => {
    const data = response?.data || {};
    return (
        data.answer ||
        data.message ||
        data.content ||
        data.object?.answer ||
        data.object?.message ||
        data.object?.content ||
        data.choices?.[0]?.message?.content ||
        ''
    );
};

const getErrorContent = err => {
    const error = err?.response?.data?.error;
    return (
        err?.response?.data?.message ||
        error?.message ||
        (typeof error === 'string' ? error : '') ||
        'I could not reach the AI assistant right now. Please try again in a moment.'
    );
};

const compactJson = value => JSON.stringify(value, null, 2);

export default function ChatbotWidget({ proxyEndpoint, apiKey }) {
    const location = useLocation();
    const classes = useStyles();
    const { systemPrompt, quickPrompts, knowledgebaseIndex, knowledgebase, getKnowledgeDocument, searchKnowledge } = useAiDocs();

    // Node reference required for react-draggable
    const draggableNodeRef = useRef(null);

    const storedUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    const sanitizeUser = userDetail => ({
        id: userDetail?.id || userDetail?.userSub || '',
        email: userDetail?.email || userDetail?.username || '',
        name: userDetail?.name || userDetail?.givenName || '',
        userType: userDetail?.userType || '',
        organizationId: ["BUSINESS_ADMIN"].includes(userDetail?.userType) ? (organizations?.[0]?.id || "") : (userDetail?.providerInformation?.organizationId || userDetail?.organizationId || ''),
        isAdmin: ["BUSINESS_ADMIN"].includes(userDetail?.userType) ? true : (userDetail?.providerInformation?.isAdmin || false),
        roles: userDetail?.providerInformation?.roles || ''
    });

    const reduxUser = useSelector(state => state.user.userDetail);
    const organizations = useSelector(state => state.organization.organizations);

    const userDetail = Object.keys(reduxUser || {}).length ? reduxUser : storedUser;
    const safeUser = useMemo(() => sanitizeUser(userDetail), [userDetail]);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState(createInitialMessages);
    const messagesEndRef = useRef(null);
    const [maximized, setMaximized] = useState(false);

    // Default starting point (bottom right corner)
    const [position, setPosition] = useState(() => {
        const zoom = 0.9;//getZoomFactor();
        const scaledWidth = window.innerWidth / zoom;
        const scaledHeight = window.innerHeight / zoom;

        return {
            x: scaledWidth - 504,  // Accounts for the 480px width + spacing
            y: scaledHeight - 744  // Accounts for the 720px height + spacing
        };
    });

    useEffect(() => {
        if (open && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, loading, open]);

    const resetConversation = () => {
        setMessages(createInitialMessages());
        setMessage('');
    };

    const snapToSide = (x, y) => {
        const width = 480;
        const margin = 24;
        const zoom = 0.9;// getZoomFactor();

        // 1. Normalize the viewport dimensions based on zoom
        const scaledVw = window.innerWidth / zoom;
        const scaledVh = window.innerHeight / zoom;

        // 2. Snap safely using the scaled constraints
        setPosition({
            x: x + width / 2 < scaledVw / 2
                ? margin
                : scaledVw - width - margin,
            y: Math.max(margin, Math.min(y, scaledVh - 744))
        });
    };

    const buildSystemMessage = (messages) => {
        const context = {
            product: 'Quezone Onboarding',
            region: REGION,
            currentPath: location.pathname,
            user: safeUser,
            organizations: (organizations || []).slice(0, 10).map(org => ({
                id: org.id,
                name: org.name,
                description: org.description,
                settings: org?.preferences,
                bookingUrl: {
                    customerPortal: `${CUSTWEB_ROOT}${org.orgRef}`,
                    quickBook: `${CUSTWEB_ROOT}quick-book/${org.orgRef}`,
                },
                timezone: org?.preferences?.timezoneId
            })),
        };

        const docs = searchKnowledge((messages.at(-1).content));
        const knowledgeContext = docs
            .map(id => getKnowledgeDocument(id.id))
            .join("\n\n");
        const knowledgeContextPrompt = knowledgeContext ?
            "\n\nRefer These Knowledge Base Documents :  \n --- Start of Knowledge Docs --- \n" + knowledgeContext + "\n\n --- End of Knowledge Docs ---\n\n" : "";

        return {
            role: 'system',
            content: `${systemPrompt}${knowledgeContextPrompt}\n\nUse this current app context when helpful:\n${compactJson(context)}`
        };
    };

    const buildPayload = nextMessages => ({
        model: OPENROUTER_MODEL,
        messages: [
            buildSystemMessage(nextMessages),
            ...nextMessages.slice(-16)
        ],
        temperature: 0.7,
        max_tokens: 2048,
        stream: false,
    });

    const buildHeaders = () => ({
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'Quezone Onboarding'
    });

    const handleSend = async overrideMessage => {
        const content = (overrideMessage || message).trim();
        if (!content || loading) return;

        const userMessage = { role: 'user', content };
        const nextMessages = [...messages, userMessage];

        setMessages(nextMessages);
        setMessage('');
        setLoading(true);

        try {
            const response = await axios.post(proxyEndpoint, buildPayload(nextMessages), {
                headers: buildHeaders()
            });
            const assistantContent = getAssistantContent(response);

            setMessages(prev => [
                ...prev,
                {
                    role: 'assistant',
                    content: assistantContent || 'I could not reach the AI service. Please try again.'
                }
            ]);
        } catch (err) {
            const errorMessage = getErrorContent(err);
            setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
            {!open && (
                <Tooltip title="Open Quezone Assistant">
                    <Fab
                        color="primary"
                        onClick={() => setOpen(true)}
                        style={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            zIndex: 9999
                        }}
                    >
                        <ChatIcon />
                    </Fab>
                </Tooltip>
            )}

            {open && (
                <Draggable
                    nodeRef={draggableNodeRef}
                    disabled={maximized}
                    handle=".chat-drag-handle"
                    position={maximized ? { x: 0, y: 0 } : position}
                    onStop={(e, data) => {
                        snapToSide(data.x, data.y);
                    }}
                >
                    <Paper
                        ref={draggableNodeRef}
                        elevation={8}
                        style={{
                            position: 'fixed',
                            zIndex: 9999,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            top: 0,
                            left: 0,
                            ...(maximized
                                ? {
                                    width: '100vw',
                                    height: '100vh',
                                    maxWidth: '100vw',
                                    maxHeight: '100vh',
                                    borderRadius: 0,
                                    zoom: 1.1
                                }
                                : {
                                    width: 480,
                                    maxWidth: 'calc(100vw - 32px)',
                                    height: 720,
                                    maxHeight: 'calc(100vh - 48px)',
                                    borderRadius: 8,
                                    zoom: 1
                                })
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            bgcolor="primary.main"
                            color="white"
                            p={2}
                        >
                            <Box minWidth={0}>
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    Quezone Assistant
                                </Typography>
                                <Typography variant="caption" style={{ opacity: 0.85 }}>
                                    Admin setup agent
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center">
                                {/*
                            <Tooltip 
                                title="Start a new chat"
                                PopperProps={{ className: classes.tooltipPopper }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={resetConversation}
                                    style={{ color: 'white', marginRight: 4 }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            */}
                                <Tooltip
                                    title="Move window"
                                    PopperProps={{ className: classes.tooltipPopper }}
                                >
                                    {/* Added cursor pointer to explicitly state drag capabilities */}
                                    <IconButton
                                        size="small"
                                        className="chat-drag-handle"
                                        style={{ color: 'white', cursor: maximized ? 'default' : 'move' }}
                                    >
                                        <MoveIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title={maximized ? "Restore" : "Maximize"}
                                    PopperProps={{ className: classes.tooltipPopper }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setMaximized(!maximized)}
                                        style={{ color: "white", marginRight: 4 }}
                                    >
                                        {maximized ? (
                                            <FullscreenExitIcon fontSize="small" />
                                        ) : (
                                            <FullscreenIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title="Minimize assistant"
                                    PopperProps={{ className: classes.tooltipPopper }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setOpen(false)}
                                        style={{ color: 'white' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box
                            px={2}
                            py={1}
                            borderBottom="1px solid #e0e0e0"
                            style={{
                                background: '#fff',
                                display: 'flex',
                                gap: 8,
                                overflowX: 'auto'
                            }}
                        >
                            {quickPrompts.map(prompt => (
                                <Chip
                                    key={prompt}
                                    label={prompt}
                                    size="small"
                                    clickable
                                    disabled={loading}
                                    onClick={() => handleSend(prompt)}
                                    style={{ flexShrink: 0 }}
                                />
                            ))}
                        </Box>

                        <Box
                            flex={1}
                            p={2}
                            style={{
                                overflowY: 'auto',
                                background: '#f5f7fa'
                            }}
                        >
                            {messages.map((msg, index) => (
                                <Box
                                    key={`${msg.role}-${index}`}
                                    display="flex"
                                    justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                                    mb={1.5}
                                >
                                    <Box
                                        style={{
                                            maxWidth: '84%',
                                            padding: '10px 14px',
                                            borderRadius: 8,
                                            background: msg.role === 'user' ? '#1976d2' : '#ffffff',
                                            color: msg.role === 'user' ? '#fff' : '#333',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            //whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {renderMarkDown(msg.content)}
                                    </Box>
                                </Box>
                            ))}

                            {loading && (
                                <Box display="flex" alignItems="center" mt={1}>
                                    <CircularProgress size={16} style={{ marginRight: 8 }} />
                                    <Typography variant="body2">
                                        Assistant is thinking...
                                    </Typography>
                                </Box>
                            )}

                            <div ref={messagesEndRef} />
                        </Box>

                        <Box
                            p={1.5}
                            borderTop="1px solid #e0e0e0"
                            display="flex"
                            alignItems="flex-end"
                            style={{ background: '#fff' }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                rowsMax={4}
                                size="small"
                                variant="outlined"
                                placeholder="Ask about setup, services, providers, or schedules..."
                                value={message}
                                disabled={loading}
                                onChange={e => setMessage(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />

                            <Tooltip title="Send message">
                                <span>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleSend()}
                                        disabled={loading || !message.trim()}
                                        style={{ marginLeft: 4 }}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Paper>
                </Draggable>
            )}
        </>
    );
}

ChatbotWidget.propTypes = {
    proxyEndpoint: PropTypes.string,
    apiKey: PropTypes.string
};

ChatbotWidget.defaultProps = {
    proxyEndpoint: URL.AI_AGENT_CHAT,
    apiKey: AI_AGENT_API_KEY
};